import { useEffect, useState } from 'react';
import type { WidgetSettingsProps } from '@frontend/widgets/types';
import {
	createAiProfile,
	deleteAiProfile,
	listAiProfiles,
	selectAiProfile,
	testAiProfile,
	type AiProfile,
} from '@frontend/api-clients/aiProfilesClient';

export default function AIStoryWriterSettings({
	contextType,
	contextId,
	ecosystemId,
	onClose,
}: WidgetSettingsProps) {
	const projectId = contextType === 'project' ? contextId : '';
	const [profiles, setProfiles] = useState<AiProfile[]>([]);
	const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
	const [allowedOrigins, setAllowedOrigins] = useState<string[]>([]);
	const [available, setAvailable] = useState(true);
	const [busy, setBusy] = useState(false);
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');

	const [name, setName] = useState('');
	const [provider, setProvider] = useState<'openai' | 'ollama'>('openai');
	const [baseUrl, setBaseUrl] = useState('');
	const [model, setModel] = useState('');
	const [apiKey, setApiKey] = useState('');
	const [scope, setScope] = useState<'personal' | 'project'>('personal');

	const reload = async () => {
		const data = await listAiProfiles({ ecosystemId, projectId: projectId || undefined });
		setAvailable(data.available);
		setProfiles(data.profiles || []);
		setSelectedProfileId(data.selectedProfileId);
		setAllowedOrigins(data.allowedOrigins || []);
		if (data.message) setMessage(data.message);
	};

	useEffect(() => {
		reload().catch((err: Error) => setError(err.message));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ecosystemId, projectId]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onClose]);

	const handleCreate = async () => {
		setBusy(true);
		setError('');
		setMessage('');
		try {
			await createAiProfile({
				scope,
				name,
				provider,
				baseUrl,
				model,
				apiKey: provider === 'openai' ? apiKey : apiKey || undefined,
				projectId: scope === 'project' ? projectId : undefined,
				ecosystemId: scope === 'project' ? ecosystemId : undefined,
			});
			setName('');
			setApiKey('');
			setModel('');
			setBaseUrl('');
			setMessage('Connection profile saved.');
			await reload();
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	};

	const handleSelect = async (profileId: string) => {
		if (!projectId) return;
		setBusy(true);
		setError('');
		try {
			await selectAiProfile({ ecosystemId, projectId, profileId });
			setSelectedProfileId(profileId);
			setMessage('Active profile updated for this project.');
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	};

	const handleTest = async (profileId: string) => {
		setBusy(true);
		setError('');
		try {
			const result = await testAiProfile(profileId);
			setMessage(result.message || 'Connection OK');
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	};

	const handleDelete = async (profileId: string) => {
		setBusy(true);
		setError('');
		try {
			await deleteAiProfile(profileId);
			await reload();
			setMessage('Profile deleted.');
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="widget-settings-overlay" role="presentation" onClick={onClose}>
			<div
				className="widget-settings-dialog"
				role="dialog"
				aria-modal="true"
				aria-labelledby="ai-settings-title"
				onClick={(event) => event.stopPropagation()}
			>
				<div className="widget-settings-header">
					<h3 id="ai-settings-title">AI connection settings</h3>
					<button type="button" className="btn" onClick={onClose} aria-label="Close settings">
						✕
					</button>
				</div>

				{!available ? (
					<p className="tree-dashboard-muted">
						AI profiles require PostgreSQL and an operator-configured encryption key. Ask a system
						admin to enable them, or use the legacy AI_PROVIDER environment mode.
					</p>
				) : null}

				{error ? <div className="settings-message settings-error">{error}</div> : null}
				{message ? <div className="settings-message settings-success">{message}</div> : null}

				<section className="widget-settings-section">
					<h4>Available profiles</h4>
					{profiles.length === 0 ? (
						<p className="tree-dashboard-muted">No profiles yet.</p>
					) : (
						<ul className="widget-list">
							{profiles.map((profile) => (
								<li key={profile.id} className="widget-list-item">
									<div className="widget-list-primary">
										<strong>{profile.name}</strong>
										{selectedProfileId === profile.id ? (
											<span className="widget-status-badge">Active</span>
										) : null}
									</div>
									<div className="widget-list-meta">
										<span>{profile.scope}</span>
										<span>{profile.provider}</span>
										<span>{profile.model || 'default model'}</span>
									</div>
									<div className="ai-profile-actions">
										{projectId ? (
											<button
												type="button"
												className="btn btn-primary"
												disabled={busy}
												onClick={() => handleSelect(profile.id)}
											>
												Use for this project
											</button>
										) : null}
										<button
											type="button"
											className="btn"
											disabled={busy}
											onClick={() => handleTest(profile.id)}
										>
											Test
										</button>
										{profile.scope === 'personal' || profile.scope === 'project' ? (
											<button
												type="button"
												className="btn"
												disabled={busy}
												onClick={() => handleDelete(profile.id)}
											>
												Delete
											</button>
										) : null}
									</div>
								</li>
							))}
						</ul>
					)}
				</section>

				<section className="widget-settings-section">
					<h4>Add connection</h4>
					<p className="tree-dashboard-muted">
						Enter any OpenAI-compatible or Ollama base URL your account can reach (including a
						self-hosted GPU host). API keys are stored encrypted in the portal database for your
						profile — not in docker-compose.
					</p>
					{allowedOrigins.length > 0 ? (
						<p className="tree-dashboard-muted">
							This realm currently restricts endpoints to: {allowedOrigins.join(', ')}.
						</p>
					) : null}
					<div className="ai-story-field-row">
						<label htmlFor="ai-profile-name">Name</label>
						<input id="ai-profile-name" value={name} onChange={(e) => setName(e.target.value)} />
					</div>
					<div className="ai-story-field-row">
						<label htmlFor="ai-profile-scope">Scope</label>
						<select
							id="ai-profile-scope"
							value={scope}
							onChange={(e) => setScope(e.target.value as 'personal' | 'project')}
							disabled={!projectId}
						>
							<option value="personal">Personal</option>
							<option value="project">Project shared</option>
						</select>
					</div>
					<div className="ai-story-field-row">
						<label htmlFor="ai-profile-provider">Provider</label>
						<select
							id="ai-profile-provider"
							value={provider}
							onChange={(e) => setProvider(e.target.value as 'openai' | 'ollama')}
						>
							<option value="openai">OpenAI-compatible</option>
							<option value="ollama">Ollama</option>
						</select>
					</div>
					<div className="ai-story-field-row">
						<label htmlFor="ai-profile-url">Base URL</label>
						<input
							id="ai-profile-url"
							value={baseUrl}
							onChange={(e) => setBaseUrl(e.target.value)}
							placeholder="https://api.openai.com/v1"
						/>
					</div>
					<div className="ai-story-field-row">
						<label htmlFor="ai-profile-model">Model</label>
						<input
							id="ai-profile-model"
							value={model}
							onChange={(e) => setModel(e.target.value)}
							placeholder="gpt-4o-mini or qwen2.5:7b"
						/>
					</div>
					{provider === 'openai' ? (
						<div className="ai-story-field-row">
							<label htmlFor="ai-profile-key">API key</label>
							<input
								id="ai-profile-key"
								type="password"
								value={apiKey}
								onChange={(e) => setApiKey(e.target.value)}
								autoComplete="off"
							/>
						</div>
					) : null}
					<button
						type="button"
						className="btn btn-primary"
						disabled={busy || !name.trim() || !baseUrl.trim()}
						onClick={handleCreate}
					>
						Save profile
					</button>
				</section>
			</div>
		</div>
	);
}
